import React, { useState, useMemo } from "react";

export default function RSVPAdminDashboard({ rsvps = [], loading = false, onRefresh }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("all");
  const [parentFilter, setParentFilter] = useState("all");
  const [viewMode, setViewMode] = useState("attendees"); // "attendees" | "schools"
  const [expandedSchool, setExpandedSchool] = useState(null);

  // ── Metrics & Calculations ─────────────────────────────────────────────────
  const {
    totalStudents,
    totalParents,
    totalHeadcount,
    totalRegistrations,
    totalSchools,
    schoolsList,
  } = useMemo(() => {
    const students = rsvps.length;

    // 1. Group submissions map (to avoid double-counting parents within the same group)
    const groups = new Map();
    rsvps.forEach((r) => {
      const gId = r.groupId || r.id;
      if (!groups.has(gId)) {
        groups.set(gId, {
          groupId: gId,
          school: (r.school || "").trim(),
          parentsAttending: Boolean(r.parentsAttending),
          parentCount: Number(r.numberOfParents ?? r.parentCount ?? 0) || 0,
          createdAt: r.createdAt,
          members: [],
        });
      }
      groups.get(gId).members.push(r);
    });

    // 2. Total parents (sum of parent counts per unique group submission)
    let parents = 0;
    groups.forEach((grp) => {
      if (grp.parentsAttending) {
        parents += grp.parentCount;
      }
    });

    // 3. School breakdown
    const schools = new Map();
    rsvps.forEach((r) => {
      const rawSchool = (r.school || "Unknown School").trim();
      const key = rawSchool.toLowerCase();
      if (!schools.has(key)) {
        schools.set(key, {
          name: rawSchool,
          studentCount: 0,
          parentCount: 0,
          registrationCount: 0,
          students: [],
          groupIds: new Set(),
        });
      }
      const s = schools.get(key);
      s.studentCount += 1;
      s.students.push(r);
      s.groupIds.add(r.groupId || r.id);
    });

    // Add parent counts and registration counts to schools
    groups.forEach((grp) => {
      const key = (grp.school || "Unknown School").trim().toLowerCase();
      if (schools.has(key)) {
        const s = schools.get(key);
        s.registrationCount += 1;
        if (grp.parentsAttending) {
          s.parentCount += grp.parentCount;
        }
      }
    });

    const sortedSchools = Array.from(schools.values()).sort(
      (a, b) => b.studentCount - a.studentCount || a.name.localeCompare(b.name)
    );

    return {
      totalStudents: students,
      totalParents: parents,
      totalHeadcount: students + parents,
      totalRegistrations: groups.size,
      totalSchools: schools.size,
      schoolsList: sortedSchools,
    };
  }, [rsvps]);

  // ── Filtered attendees ─────────────────────────────────────────────────────
  const filteredAttendees = useMemo(() => {
    let result = [...rsvps];

    // Filter by school
    if (selectedSchool !== "all") {
      result = result.filter(
        (r) => (r.school || "").trim().toLowerCase() === selectedSchool.toLowerCase()
      );
    }

    // Filter by parent attendance
    if (parentFilter === "with_parents") {
      result = result.filter((r) => Boolean(r.parentsAttending));
    } else if (parentFilter === "without_parents") {
      result = result.filter((r) => !r.parentsAttending);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (r) =>
          (r.name && r.name.toLowerCase().includes(q)) ||
          (r.school && r.school.toLowerCase().includes(q)) ||
          (r.email && r.email.toLowerCase().includes(q)) ||
          (r.phone && r.phone.toLowerCase().includes(q)) ||
          (r.grade && r.grade.toLowerCase().includes(q)) ||
          (r.groupId && r.groupId.toLowerCase().includes(q))
      );
    }

    return result;
  }, [rsvps, selectedSchool, parentFilter, searchQuery]);

  // ── CSV Exports ────────────────────────────────────────────────────────────
  const exportAttendeesCSV = () => {
    if (filteredAttendees.length === 0) {
      alert("No attendees to export.");
      return;
    }

    const headers = [
      "#",
      "Registration Date",
      "Registration Time",
      "School / Institution",
      "Full Name",
      "Grade",
      "Contact Number",
      "Email Address",
      "Parents Coming",
      "Number of Parents",
      "Group Reference",
      "Group Size",
      "Position in Group",
    ];

    const rows = filteredAttendees.map((r, i) => {
      const d = r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt || Date.now());
      const dateStr = d.toLocaleDateString();
      const timeStr = d.toLocaleTimeString();
      const parentsComing = r.parentsAttending ? "YES" : "NO";
      const parentNum = r.parentsAttending ? (r.numberOfParents ?? r.parentCount ?? 0) : 0;

      return [
        i + 1,
        dateStr,
        timeStr,
        r.school || "",
        r.name || "",
        r.grade || "",
        r.phone || "",
        r.email || "",
        parentsComing,
        parentNum,
        r.groupId || r.id || "",
        r.groupSize || 1,
        r.positionInGroup || 1,
      ].map((val) => {
        const str = String(val ?? "").replace(/"/g, '""');
        return `"${str}"`;
      });
    });

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    downloadCSV(csvContent, "btui26_rsvp_attendees.csv");
  };

  const exportSchoolsSummaryCSV = () => {
    if (schoolsList.length === 0) {
      alert("No school data to export.");
      return;
    }

    const headers = [
      "#",
      "School / Institution",
      "Total Students",
      "Total Parents",
      "Total Headcount",
      "Registration Submissions",
    ];

    const rows = schoolsList.map((s, i) => [
      i + 1,
      s.name,
      s.studentCount,
      s.parentCount,
      s.studentCount + s.parentCount,
      s.registrationCount,
    ].map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`));

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    downloadCSV(csvContent, "btui26_rsvp_schools_summary.csv");
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDate = (val) => {
    if (!val) return "N/A";
    const d = val instanceof Date ? val : new Date(val);
    return isNaN(d.getTime())
      ? "N/A"
      : `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  };

  return (
    <div className="rsvp-admin-dashboard">
      {/* ── KPI Stat Cards ─────────────────────────────────────────────────── */}
      <div className="rsvp-kpi-grid">
        <div className="rsvp-kpi-card rsvp-kpi-students">
          <div className="rsvp-kpi-header">
            <span className="rsvp-kpi-label">TOTAL STUDENTS</span>
            <span className="rsvp-kpi-icon">🎓</span>
          </div>
          <div className="rsvp-kpi-value">{totalStudents}</div>
          <div className="rsvp-kpi-sub">Registered student attendees</div>
        </div>

        <div className="rsvp-kpi-card rsvp-kpi-parents">
          <div className="rsvp-kpi-header">
            <span className="rsvp-kpi-label">TOTAL PARENTS</span>
            <span className="rsvp-kpi-icon">👨‍👩‍👧</span>
          </div>
          <div className="rsvp-kpi-value">{totalParents}</div>
          <div className="rsvp-kpi-sub">Accompanying parents / guardians</div>
        </div>

        <div className="rsvp-kpi-card rsvp-kpi-headcount">
          <div className="rsvp-kpi-header">
            <span className="rsvp-kpi-label">TOTAL HEADCOUNT</span>
            <span className="rsvp-kpi-icon">🎟️</span>
          </div>
          <div className="rsvp-kpi-value">{totalHeadcount}</div>
          <div className="rsvp-kpi-sub">Students + Parents combined</div>
        </div>

        <div className="rsvp-kpi-card rsvp-kpi-registrations">
          <div className="rsvp-kpi-header">
            <span className="rsvp-kpi-label">REGISTRATIONS</span>
            <span className="rsvp-kpi-icon">📝</span>
          </div>
          <div className="rsvp-kpi-value">{totalRegistrations}</div>
          <div className="rsvp-kpi-sub">RSVP submission batches</div>
        </div>

        <div className="rsvp-kpi-card rsvp-kpi-schools">
          <div className="rsvp-kpi-header">
            <span className="rsvp-kpi-label">SCHOOLS</span>
            <span className="rsvp-kpi-icon">🏫</span>
          </div>
          <div className="rsvp-kpi-value">{totalSchools}</div>
          <div className="rsvp-kpi-sub">Institutions represented</div>
        </div>
      </div>

      {/* ── Controls & Filters Bar ─────────────────────────────────────────── */}
      <div className="admin-controls rsvp-admin-controls">
        <div className="filter-section rsvp-search-section">
          <label htmlFor="rsvp-search">Search:</label>
          <input
            id="rsvp-search"
            type="text"
            className="rsvp-search-input"
            placeholder="Search student, school, email, phone, ref..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="rsvp-clear-search-btn"
              onClick={() => setSearchQuery("")}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="filter-section">
          <label htmlFor="rsvp-school-filter">School:</label>
          <select
            id="rsvp-school-filter"
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
          >
            <option value="all">All Schools ({schoolsList.length})</option>
            {schoolsList.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name} ({s.studentCount} student{s.studentCount !== 1 ? "s" : ""})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-section">
          <label htmlFor="rsvp-parent-filter">Parents:</label>
          <select
            id="rsvp-parent-filter"
            value={parentFilter}
            onChange={(e) => setParentFilter(e.target.value)}
          >
            <option value="all">All Attendees ({rsvps.length})</option>
            <option value="with_parents">With Parents ({rsvps.filter((r) => r.parentsAttending).length})</option>
            <option value="without_parents">Students Only ({rsvps.filter((r) => !r.parentsAttending).length})</option>
          </select>
        </div>

        <div className="rsvp-view-toggle">
          <button
            type="button"
            className={`rsvp-toggle-btn ${viewMode === "attendees" ? "active" : ""}`}
            onClick={() => setViewMode("attendees")}
          >
            Attendees ({filteredAttendees.length})
          </button>
          <button
            type="button"
            className={`rsvp-toggle-btn ${viewMode === "schools" ? "active" : ""}`}
            onClick={() => setViewMode("schools")}
          >
            School Summary ({schoolsList.length})
          </button>
        </div>

        <div className="rsvp-action-buttons">
          <button
            type="button"
            className="export-btn"
            onClick={viewMode === "schools" ? exportSchoolsSummaryCSV : exportAttendeesCSV}
            title="Download CSV"
          >
            Export {viewMode === "schools" ? "Schools" : "Attendees"} CSV
          </button>
          <button
            type="button"
            className="refresh-btn"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh RSVPs"}
          </button>
        </div>
      </div>

      {/* ── Content View ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="loading">Loading RSVP registrations…</div>
      ) : viewMode === "schools" ? (
        /* ── School Summary View ─────────────────────────────────────────── */
        <div className="responses-container rsvp-schools-container">
          <div className="rsvp-table-header">
            <h2>School Breakdown ({schoolsList.length} Schools)</h2>
            <button
              type="button"
              className="export-btn rsvp-sm-btn"
              onClick={exportSchoolsSummaryCSV}
            >
              Export Schools CSV
            </button>
          </div>

          {schoolsList.length === 0 ? (
            <p className="no-data">No schools found.</p>
          ) : (
            <div className="orders-table-wrapper">
              <table className="orders-table rsvp-schools-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>School / Institution</th>
                    <th>Students</th>
                    <th>Parents</th>
                    <th>Total Headcount</th>
                    <th>Submissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schoolsList.map((s, idx) => {
                    const isExpanded = expandedSchool === s.name;
                    return (
                      <React.Fragment key={s.name}>
                        <tr>
                          <td>{idx + 1}</td>
                          <td className="customer-cell">
                            <strong>{s.name}</strong>
                          </td>
                          <td>
                            <span className="rsvp-badge-count">{s.studentCount}</span>
                          </td>
                          <td>
                            <span className={`rsvp-badge-count ${s.parentCount > 0 ? "rsvp-badge-highlight" : ""}`}>
                              {s.parentCount}
                            </span>
                          </td>
                          <td>
                            <strong className="rsvp-headcount-val">
                              {s.studentCount + s.parentCount}
                            </strong>
                          </td>
                          <td>{s.registrationCount}</td>
                          <td>
                            <div className="rsvp-row-actions">
                              <button
                                type="button"
                                className="rsvp-action-link"
                                onClick={() => {
                                  setSelectedSchool(s.name);
                                  setViewMode("attendees");
                                }}
                              >
                                View Students
                              </button>
                              <button
                                type="button"
                                className="rsvp-action-link rsvp-expand-link"
                                onClick={() => setExpandedSchool(isExpanded ? null : s.name)}
                              >
                                {isExpanded ? "Hide List ▲" : `Show (${s.students.length}) ▼`}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="rsvp-expanded-row">
                            <td colSpan={7}>
                              <div className="rsvp-school-student-list">
                                <div className="rsvp-expanded-title">
                                  Students from <strong>{s.name}</strong> ({s.students.length}):
                                </div>
                                <div className="rsvp-student-pill-grid">
                                  {s.students.map((st, sIdx) => (
                                    <div key={st.id || sIdx} className="rsvp-student-pill">
                                      <span className="rsvp-pill-name">{st.name}</span>
                                      <span className="rsvp-pill-grade">Gr. {st.grade || "N/A"}</span>
                                      {st.phone && (
                                        <a href={`tel:${st.phone}`} className="rsvp-pill-contact">
                                          📞 {st.phone}
                                        </a>
                                      )}
                                      {st.parentsAttending && (
                                        <span className="rsvp-pill-parents">
                                          +{st.numberOfParents ?? st.parentCount ?? 1} parent
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* ── Individual Attendees View ───────────────────────────────────── */
        <div className="responses-container rsvp-attendees-container">
          <div className="rsvp-table-header">
            <h2>
              Attendee Registrations ({filteredAttendees.length}
              {filteredAttendees.length !== rsvps.length ? ` of ${rsvps.length}` : ""})
            </h2>
            <div className="rsvp-header-meta">
              <span>{totalParents} total parents attending</span>
              <span className="rsvp-meta-dot">•</span>
              <span>{totalHeadcount} total venue attendees</span>
            </div>
          </div>

          {filteredAttendees.length === 0 ? (
            <p className="no-data">
              {rsvps.length === 0
                ? "No RSVP registrations yet."
                : "No attendees match the selected search or filters."}
            </p>
          ) : (
            <div className="orders-table-wrapper">
              <table className="orders-table rsvp-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Registered</th>
                    <th>Attendee Name</th>
                    <th>School / Institution</th>
                    <th>Grade</th>
                    <th>Contact</th>
                    <th>Parents Coming</th>
                    <th>Group Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendees.map((attendee, index) => {
                    const isWithParents = Boolean(attendee.parentsAttending);
                    const pCount = attendee.numberOfParents ?? attendee.parentCount ?? (isWithParents ? 1 : 0);

                    return (
                      <tr key={attendee.id || index}>
                        <td>{index + 1}</td>
                        <td className="date-cell">
                          {formatDate(attendee.createdAt)}
                        </td>
                        <td className="customer-cell">
                          <strong className="rsvp-attendee-name">{attendee.name || "N/A"}</strong>
                        </td>
                        <td>
                          <span className="rsvp-school-badge">{attendee.school || "N/A"}</span>
                        </td>
                        <td>
                          <span className="rsvp-grade-tag">{attendee.grade || "N/A"}</span>
                        </td>
                        <td className="rsvp-contact-cell">
                          {attendee.phone ? (
                            <div>
                              <a href={`tel:${attendee.phone}`} className="rsvp-phone-link">
                                📞 {attendee.phone}
                              </a>
                            </div>
                          ) : (
                            <span className="no-data">No phone</span>
                          )}
                          {attendee.email && (
                            <div>
                              <a href={`mailto:${attendee.email}`} className="rsvp-email-link">
                                ✉ {attendee.email}
                              </a>
                            </div>
                          )}
                        </td>
                        <td>
                          {isWithParents ? (
                            <span className="rsvp-status-badge-parents">
                              ✓ YES ({pCount} parent{pCount !== 1 ? "s" : ""})
                            </span>
                          ) : (
                            <span className="rsvp-status-badge-noparents">NO</span>
                          )}
                        </td>
                        <td>
                          <span className="rsvp-group-ref" title={attendee.groupId || attendee.id}>
                            {attendee.groupId
                              ? `${attendee.groupId.slice(0, 8)}… (${attendee.positionInGroup || 1}/${attendee.groupSize || 1})`
                              : "Solo"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
